<script setup>
import { onMounted, ref, defineProps, defineEmits, defineExpose, computed, watch, nextTick } from 'vue';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  type: {
    type: String,
    default: 'text',
    validator: (value) => ['text', 'number', 'date', 'datetime-local', 'time'].includes(value)
  },
  id: {
    type: String,
    required: true
  },
  placeholder: {
    type: String,
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  pattern: {
    type: String,
    default: ''
  },
  isTextarea: {
    type: Boolean,
    default: false
  },
  rows: {
    type: Number,
    default: 3
  },
  autoResize: {
    type: Boolean,
    default: false
  },
  autocomplete: {
    type: String,
    default: 'off'
  },
  dataTestId: {
    type: String,
    default: ''
  },
  min: {
    type: [String, Number],
    default: null
  },
  max: {
    type: [String, Number],
    default: null
  },
  step: {
    type: [String, Number],
    default: null

  },
  badges: {
    type: Array,
    default: () => []
  },
  bgClass: {
    type: String,
    default: 'bg-echoes-background-black'
  },
  borderClass: {
    type: String,
    default: 'border-echoes-border-gray'
  },
  hoverBgClass: {
    type: String,
    default: 'bg-echoes-background-black'
  },
  hoverBorderClass: {
    type: String,
    default: 'border-white/20'
  },
  hoverTextClass: {
    type: String,
    default: 'text-white'
  },
  focusBgClass: {
    type: String,
    default: 'bg-echoes-background-black'
  },
  focusBorderClass: {
    type: String,
    default: 'border-echoes-primary'
  },
  badgeBgClass: {
    type: String,
    default: 'bg-echoes-primary'
  },
  badgeTextClass: {
    type: String,
    default: 'text-white'
  }
});

defineEmits(['update:modelValue', 'input', 'keydown', 'remove-badge']);

const input = ref(null);

const fieldClasses = computed(() => {
  return [
    'text-input__field',
    props.bgClass,
    props.borderClass,
    `hover:${props.hoverBgClass}`,
    `hover:${props.hoverBorderClass}`,
    `hover:${props.hoverTextClass}`,
    `focus:${props.focusBgClass}`,
    `focus:${props.focusBorderClass}`
  ];
});

const badgeClasses = computed(() => {
  return [
    'text-input__badge',
    props.badgeBgClass,
    props.badgeTextClass
  ];
});

const adjustTextareaHeight = () => {
  if (!props.autoResize || !props.isTextarea || !input.value) return;
  
  input.value.style.height = 'auto';
  input.value.style.height = `${input.value.scrollHeight}px`;
};

onMounted(() => {
  if (input.value?.hasAttribute('autofocus')) {
    input.value?.focus();
  }
  
  if (props.autoResize && props.isTextarea) {
    nextTick(() => adjustTextareaHeight());
  }
});

watch(() => props.modelValue, () => {
  if (props.autoResize && props.isTextarea) {
    nextTick(() => adjustTextareaHeight());
  }
});

defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <div class="text-input">
    <div v-if="$slots.icon" class="text-input__icon-container">
      <slot name="icon"></slot>
    </div>
    
    <!-- Badges container -->
    <div v-if="badges.length > 0 && !isTextarea" class="text-input__badges-container">
      <div 
        v-for="(badge, index) in badges" 
        :key="index"
        :class="badgeClasses"
      >
        <span class="text-input__badge-text">"{{ badge.text }}"</span>
        <button 
          v-if="badge.removable !== false"
          @click="$emit('remove-badge', index)"
          class="text-input__badge-remove"
          type="button"
        >
          <i class="uil uil-times"></i>
        </button>
      </div>
    </div>
    
    <div v-if="isTextarea" class="text-input__textarea-wrapper">
      <textarea
        ref="input"
        :value="modelValue"
        @input="$emit('update:modelValue', $event.target.value); $emit('input', $event); adjustTextareaHeight()"
        @keydown="$emit('keydown', $event)"
        :id="id"
        :placeholder="!modelValue ? '' : placeholder"
        :required="required"
        :disabled="disabled"
        :rows="rows"
        :autocomplete="autocomplete"
        :data-testid="dataTestId"
        :class="[
          ...fieldClasses,
          'text-input__textarea',
          autoResize ? 'text-input__textarea--auto-resize' : '',
          $slots.icon ? 'text-input__field--with-icon' : '',
          disabled ? 'text-input__field--disabled' : ''
        ]"
      ></textarea>
      
      <div 
        v-if="!modelValue && placeholder"
        class="text-input__textarea-placeholder"
        :class="[
          $slots.icon ? 'text-input__textarea-placeholder--with-icon' : '',
          disabled ? 'text-input__textarea-placeholder--disabled' : ''
        ]"
        @click="input?.focus()"
      >
        {{ placeholder }}
      </div>
    </div>
    
    <input
      v-else
      ref="input"
      :type="type"
      :value="modelValue"
      @input="$emit('update:modelValue', $event.target.value); $emit('input', $event)"
      @keydown="$emit('keydown', $event)"
      :id="id"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :data-testid="dataTestId"
      v-bind="{
        ...(pattern ? { pattern } : {}),
        ...(min !== null ? { min } : {}),
        ...(max !== null ? { max } : {}),
        ...(step !== null ? { step } : {})
      }"
      :class="[
        ...fieldClasses,
        $slots.icon ? 'text-input__field--with-icon' : '',
        badges.length > 0 ? 'text-input__field--with-badges' : '',
        disabled ? 'text-input__field--disabled' : '',
        type === 'date' ? 'text-input__field--date' : ''
      ]"
    />
  </div>
</template>

<style scoped lang="postcss">
.text-input {
  @apply relative isolate;
}

.text-input__icon-container {
  @apply absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none
    text-white/80;
}

.text-input__field {
  @apply truncate w-full px-4 py-2.5 text-sm rounded-[13px] h-11
    border
    transition-all duration-200 ease-in-out;

  &::placeholder {
    @apply transition-all duration-200 text-white/50 hover:text-white/80;
  }

  &:focus::placeholder {
    @apply opacity-0 translate-x-1;
  }
  
  &:focus {
    @apply outline-none;
  }
}

.text-input__field--with-icon {
  @apply pl-11;
}

.text-input__field--with-badges {
  @apply pl-4;
}

.text-input__field--with-icon.text-input__field--with-badges {
  @apply pl-11;
}

.text-input__field--disabled {
  @apply opacity-60 cursor-not-allowed;
}

.text-input__textarea-wrapper {
  @apply relative;
}

.text-input__textarea {
  @apply resize-y min-h-[80px];
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-wrap: break-word;
  overflow: auto;
}

.text-input__textarea--auto-resize {
  @apply resize-none overflow-hidden;
  min-height: 80px;
}


.text-input__textarea-placeholder {
  @apply absolute top-0 left-0 w-full h-full px-4 py-2.5 text-sm
    text-white/50 pointer-events-none whitespace-pre-wrap
    transition-all duration-200 ease-in-out;
  
  line-height: 1.4;
  overflow: hidden;
  
  &:hover {
    @apply text-white/80;
  }
}

.text-input__textarea-placeholder--with-icon {
  @apply pl-11;
}

.text-input__textarea-placeholder--disabled {
  @apply opacity-60;
}

.text-input__field--date {
  @apply cursor-pointer;

  &::-webkit-calendar-picker-indicator {
    @apply cursor-pointer opacity-70 hover:opacity-100 transition-opacity;
    filter: invert(1) brightness(0.9);
  }

  &::-webkit-datetime-edit {
    @apply cursor-text;
  }
}

/* Badges styles */
.text-input__badges-container {
  @apply absolute inset-y-0 left-0 flex items-center gap-2 z-10 pointer-events-none;
  padding-left: 2.75rem; /* pl-11 equivalent for icon space */
}

.text-input__badge {
  @apply flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium
    pointer-events-auto;
  backdrop-filter: blur(8px);
}

.text-input__badge-text {
  @apply whitespace-nowrap;
}

.text-input__badge-remove {
  @apply flex items-center justify-center w-4 h-4 rounded-full
  text-white bg-black
}

.text-input__badge-remove i {
  @apply text-xs;
}
</style>